import { useEffect, useState } from 'react';
import UserServices from './services/userService';
import { UserRole } from '../../application/types/userRole';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes.ts';
import { useGlobalStore } from '../../application/store/store.tsx';
import {
    // Box,
    TextField,
    Select,
    MenuItem,
    Button,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    // Dialog,
    // DialogTitle,
    // DialogContent,
    // DialogActions,
    InputAdornment,
    Paper,
    Card,
    CardContent,
    Typography,
    TablePagination,
    Skeleton,
} from '@mui/material';
// import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'; // CSV file icon
// import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddIcon from '@mui/icons-material/Add';
import EmptyTablePlaceholder from '../../common/components/EmptyTablePlaceholder.tsx';
// import UploadIcon from '@mui/icons-material/Upload';
// import { useGlobalStore } from '../../application/store/store.tsx';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState('active');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    // const [isModalOpen, setIsModalOpen] = useState(false);
    // const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const {
        setMessage,
        startLoading,
        stopLoading,
        cachedUsers,
        cachedInactiveUsers,
        activeUsersCacheTime,
        inactiveUsersCacheTime,
        setCachedUsers,
        setCachedInactiveUsers,
        profile,
        userListReloadKey,
    } = useGlobalStore();

    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

    const isActiveUsersCacheValid = () => {
        if (!activeUsersCacheTime) return false;
        return Date.now() - activeUsersCacheTime < CACHE_DURATION;
    };

    const isInactiveUsersCacheValid = () => {
        if (!inactiveUsersCacheTime) return false;
        return Date.now() - inactiveUsersCacheTime < CACHE_DURATION;
    };

    useEffect(() => {
        // Check cache first before setting loading state
        if (filter === 'inactive') {
            if (isInactiveUsersCacheValid() && cachedInactiveUsers.length > 0) {
                setUsers(cachedInactiveUsers);
                setIsLoading(false);
                return;
            }
        } else {
            if (isActiveUsersCacheValid() && cachedUsers.length > 0) {
                setUsers(cachedUsers);
                setIsLoading(false);
                return;
            }
        }

        // If no valid cache, fetch from API
        fetchUsers();
    }, [filter, profile?.selectedOrganization?.id, userListReloadKey]);

    const fetchUsers = async () => {
        setIsLoading(true);
        startLoading();
        const userServices = new UserServices();
        try {
            let data: any[];
            if (filter === 'inactive') {
                data = await userServices.getInactiveUsers();
                setCachedInactiveUsers(data);
            } else {
                data = await userServices.getUsers();
                setCachedUsers(data);
            }
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
            setMessage('Kunde inte hämta användare!', 'error', 5000);
        } finally {
            setIsLoading(false);
            stopLoading();
        }
    };

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const getRoleIcon = (roles: UserRole[]) => {
        return roles.some((role) => role.roleName === 'LocalAdmin') ? (
            <AdminPanelSettingsIcon
                className="mr-2"
                titleAccess="Administratör"
            />
        ) : (
            <span></span>
        );
    };

    // const handleSelectedFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     const file = event.target.files?.[0];
    //     if (file) {
    //         setSelectedFile(file);
    //     }
    // };

    // const handleUpload = async () => {
    //     if (selectedFile) {
    //         const userServices = new UserServices();
    //         setIsLoading(true);
    //         try {
    //             const response = await userServices.uploadCsv(selectedFile);
    //             setSelectedFile(null);
    //             // closeModal();
    //             if (response.data.length > 0) {
    //                 setMessage(
    //                     'Uppladdning lyckades men vissa användare skapades ej.',
    //                     'warning',
    //                     10000
    //                 );
    //             } else {
    //                 setMessage('Uppladdning lyckades!', 'success');
    //             }
    //         } catch (error) {
    //             console.error('Error uploading file:', error);
    //             // closeModal();
    //             setMessage('Importering med .csv fil misslyckades!', 'error');
    //         } finally {
    //             fetchUsers();
    //             setIsLoading(false);
    //         }
    //     }
    // };

    // const openModal = () => setIsModalOpen(true);
    // const closeModal = () => {
    //     setSelectedFile(null);
    //     setIsModalOpen(false);
    // };

    const filteredUsers = users.filter((user) => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return (
            user.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            fullName.includes(searchQuery.toLowerCase())
        );
    });

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const paginatedData = filteredUsers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Render shimmer loader rows
    const renderSkeletonRows = () => {
        return Array.from({ length: rowsPerPage }, (_, index) => (
            <TableRow key={`skeleton-${index}`}>
                <TableCell>
                    <Skeleton variant="circular" width={24} height={24} />
                </TableCell>
                <TableCell>
                    <Skeleton variant="text" width="80%" />
                </TableCell>
                <TableCell>
                    <Skeleton variant="text" width="60%" />
                </TableCell>
                <TableCell>
                    <Skeleton variant="text" width="70%" />
                </TableCell>
            </TableRow>
        ));
    };

    return (
        <Paper className="p-3">
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Hantera användare
            </Typography>
            <Card className="mb-3 p-2">
                <CardContent className="flex gap-2 flex-wrap">
                    <TextField
                        placeholder="Sök användare..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        className="w-[250px]"
                    />

                    <Select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        size="small"
                    >
                        <MenuItem value="active">Aktiva</MenuItem>
                        <MenuItem value="inactive">Inaktiva</MenuItem>
                    </Select>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(NavigationRoutes.createUser)}
                    >
                        <AddIcon className="mr-2" /> Ny användare
                    </Button>
                    {/* <Button
                        variant="contained"
                        color="secondary"
                        onClick={openModal}
                    >
                        <UploadIcon className="mr-2" /> Importera Användare
                    </Button> */}
                </CardContent>
            </Card>
            {/* {isModalOpen && (
                <Dialog
                    open={isModalOpen}
                    onClose={closeModal}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle
                        sx={{
                            fontWeight: 'bold',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                        }}
                    >
                        <InsertDriveFileIcon />
                        Ladda upp CSV-fil
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                padding: 2,
                                width: '100%',
                                textAlign: 'center',
                                border: '1px solid #ddd',
                                borderRadius: 2,
                                backgroundColor: '#f9f9f9',
                            }}
                        >
                            <input
                                type="file"
                                accept=".csv"
                                id="file-upload"
                                hidden
                                onChange={handleSelectedFile}
                            />
                            <Button
                                variant="contained"
                                onClick={() =>
                                    document
                                        .getElementById('file-upload')
                                        ?.click()
                                }
                            >
                                <InsertDriveFileIcon sx={{ marginRight: 1 }} />
                                Välj fil
                            </Button>
                            <Typography variant="body2" sx={{ marginTop: 1 }}>
                                {selectedFile
                                    ? selectedFile.name
                                    : 'Ingen fil vald'}
                            </Typography>
                        </Box>
                    </DialogContent>

                    <DialogActions
                        sx={{ justifyContent: 'center', paddingBottom: 2 }}
                    >
                        {selectedFile && (
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleUpload}
                            >
                                🚀 Ladda upp
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={closeModal}
                        >
                            Stäng
                        </Button>
                    </DialogActions>
                </Dialog>
            )} */}
            <TableContainer component={Paper}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell>Namn</TableCell>
                            <TableCell>Användarnamn</TableCell>
                            <TableCell>Epost</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            renderSkeletonRows()
                        ) : paginatedData.length === 0 ? (
                            <EmptyTablePlaceholder
                                colSpan={4}
                                title="Inga användare att visa"
                                subtitle="Justera filter/sökning eller klicka på ‘Ny användare’ för att lägga till."
                            />
                        ) : (
                            paginatedData.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className="odd:bg-gray-100 hover:cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `${NavigationRoutes.userView}/${row.id}`,
                                            { state: { id: row.id } }
                                        )
                                    }
                                >
                                    <TableCell>
                                        {getRoleIcon(row.userRoles)}
                                    </TableCell>
                                    <TableCell>
                                        {row.firstName + '  ' + row.lastName}
                                    </TableCell>
                                    <TableCell>{row.userName}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={isLoading ? 0 : filteredUsers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Antal poster:"
                    labelDisplayedRows={({ from, to, count }) =>
                        isLoading
                            ? 'Laddar...'
                            : `${from}-${to} av ${count !== -1 ? count : `mer än ${to}`}`
                    }
                />
            </TableContainer>
        </Paper>
    );
};

export default UserManagement;
